import {
  Badge,
  Button,
  EmptyState,
  EnvBadge,
  Icon,
  Menu,
  type MenuItem,
  PageHeader,
  Skeleton,
  Textarea,
  UserInfo,
  When,
  buttonClass,
  createExitHold,
  useConfirm,
  useToast,
} from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { useReleaseProducts } from '@/features/release-config/hooks';
import { BaselineSection } from '@/features/releases/BaselineSection';
import { ChangeLogTabs } from '@/features/releases/ChangeLogTabs';
import { CopySummary } from '@/features/releases/CopyStatusButton';
import { DistributeModal } from '@/features/releases/DistributeModal';
import { PromotionTrailCard } from '@/features/releases/PromotionTrailCard';
import {
  RefPicker,
  type RefSelection,
  SelectedRefCard,
  selectedTileClass,
} from '@/features/releases/RefPicker';
import { ReleaseStatusBadge } from '@/features/releases/ReleasesTable';
import { RunAttemptsPanel } from '@/features/releases/RunAttemptsPanel';
import { RunFailurePanel } from '@/features/releases/RunFailurePanel';
import { InjectedVars, WorkflowsSection } from '@/features/releases/WorkflowsSection';
import {
  useConfirmRelease,
  useDeleteDraft,
  usePromoteRelease,
  usePublishRelease,
  useReconcileRelease,
  useRefreshRef,
  useRelease,
  useRetryWorkflowRun,
  useUpdateDraft,
  useWatchRelease,
} from '@/features/releases/hooks';
import { apiCall } from '@/lib/api';
import { celebrate } from '@/stores/welcome.store';
import { useNavigate, useParams } from '@solidjs/router';
import {
  ApiClientError,
  RELEASE_ENVIRONMENTS,
  type ReleaseEnvironment,
  adapterWorkflowFor,
  canReleaseScope,
  workflowStem,
} from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

const errorMessage = (err: unknown): string =>
  err instanceof ApiClientError ? err.error.message : 'Request failed';

/** The draft's saved ref as a RefSelection, or null when nothing is pinned yet. */
const savedRefSelection = (release: {
  ref: string;
  commitSha: string;
}): RefSelection | null =>
  release.commitSha
    ? {
        ref: release.ref,
        commitSha: release.commitSha,
        label: release.ref || release.commitSha.slice(0, 7),
      }
    : null;

const Field = (props: { label: string; children: unknown; class?: string }) => (
  <div class={props.class}>
    <dt class="text-xs uppercase tracking-wide text-neutral-500">{props.label}</dt>
    <dd class="mt-0.5 text-sm">{props.children as never}</dd>
  </div>
);

/** Running releases update live over SSE (useRelease); this just says so. */
const LiveIndicator = () => (
  <span class="inline-flex items-center gap-1.5 text-xs text-neutral-500">
    <span class="size-2 animate-pulse rounded-full bg-green-500 motion-reduce:animate-none" />
    Live
  </span>
);

/** Numbered draft step; disabled steps stay visible but inert until their dependency holds. */
const StepSection = (props: {
  n: number;
  title: string;
  disabled?: boolean;
  hint?: string;
  children: unknown;
}) => (
  <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
    <h2 class="mb-2 flex items-center gap-2 font-medium">
      <span class="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
        {props.n}
      </span>
      {props.title}
      <Show when={props.disabled && props.hint}>
        <span class="text-xs font-normal text-neutral-500">— {props.hint}</span>
      </Show>
    </h2>
    <div
      class={`transition-opacity duration-200 ease-out motion-reduce:transition-none ${
        props.disabled ? 'pointer-events-none select-none opacity-40' : ''
      }`}
    >
      {props.children as never}
    </div>
  </div>
);

export default function ReleaseDetail() {
  const params = useParams<{ releaseId: string }>();
  // The router reuses this component across /release/versions/:releaseId
  // navigations (Promote, promotion-trail hops). Mutation hooks and page state
  // bind to one release id at mount, so remount the page whenever it changes —
  // otherwise Save/Confirm on the new page would mutate the previous release.
  return (
    <Show keyed when={params.releaseId}>
      {(releaseId) => <ReleaseDetailPage releaseId={releaseId} />}
    </Show>
  );
}

function ReleaseDetailPage(props: { releaseId: string }) {
  const { session } = useAuth();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const navigate = useNavigate();

  const release = useRelease(() => props.releaseId);
  // Adapter for mapped workflow titles on the read-only card (UCH-256).
  // includeDisabled: a disabled product's own release detail must still resolve (UCH-294).
  const products = useReleaseProducts(true);
  const productOf = (productId: string) => products.data?.data.find((p) => p.id === productId);
  const workflowTitle = (productId: string, file: string): string | null => {
    const adapter = productOf(productId)?.adapter;
    return adapter ? (adapterWorkflowFor(adapter, file)?.title ?? null) : null;
  };
  // UCH-286: run-link fallback before webhook/reconcile fills the exact run URL.
  const workflowFallbackUrl = (productId: string, file: string): string => {
    const p = productOf(productId);
    return p?.repoOwner && p?.repoName
      ? `https://github.com/${p.repoOwner}/${p.repoName}/actions/workflows/${file}`
      : '';
  };
  // release-web.yml deploys each build as a Cloudflare Pages preview on branch
  // build-<N> of project <repoName>-<env>, so the alias URL is known up front.
  // ponytail: assumes the Pages project is named after the repo; add a channel field if one isn't.
  const previewUrl = (
    rel: { productId: string; environment: string; buildNumber: number },
    run: { workflowFile: string; status: string },
  ): string => {
    const p = productOf(rel.productId);
    return p?.adapter === 'web' &&
      p.repoName &&
      run.status === 'succeeded' &&
      workflowStem(run.workflowFile) === 'release-web'
      ? `https://build-${rel.buildNumber}.${p.repoName.toLowerCase()}-${rel.environment}.pages.dev`
      : '';
  };
  // Frozen commit link when captured at Confirm, else built from the product repo.
  const commitHref = (rel: { commitUrl: string; commitSha: string; productId: string }): string => {
    if (rel.commitUrl) return rel.commitUrl;
    const p = productOf(rel.productId);
    return rel.commitSha && p?.repoOwner && p?.repoName
      ? `https://github.com/${p.repoOwner}/${p.repoName}/commit/${rel.commitSha}`
      : '';
  };
  const confirm = useConfirmRelease(props.releaseId);
  const publish = usePublishRelease(props.releaseId);
  const remove = useDeleteDraft(props.releaseId);
  const update = useUpdateDraft(props.releaseId);
  const refreshRef = useRefreshRef(props.releaseId);
  const reconcile = useReconcileRelease(props.releaseId);
  const retryRun = useRetryWorkflowRun(props.releaseId);
  const watch = useWatchRelease(props.releaseId);

  const [pendingRef, setPendingRef] = createSignal<RefSelection | null>(null);
  const [distributeOpen, setDistributeOpen] = createSignal(false);
  const [copyOpen, setCopyOpen] = createSignal(false);
  // Keep the modal mounted through its exit animation after close.
  const distributeMounted = createExitHold(() => distributeOpen() || null);
  // UCH-418: Publish to testers — mobile adapter, adhoc workflow ran, FAD app
  // configured on the channel, status succeeded/published.
  const distributeChannel = (rel: { productId: string; environment: string }) =>
    productOf(rel.productId)?.channels.find((ch) => ch.environment === rel.environment);
  const canDistribute = (rel: {
    productId: string;
    environment: string;
    status: string;
    selectedWorkflows: string[];
  }): boolean =>
    (rel.status === 'succeeded' || rel.status === 'published') &&
    productOf(rel.productId)?.adapter === 'mobile_app' &&
    rel.selectedWorkflows.some((f) => workflowStem(f) === 'release-mobile-adhoc') &&
    Boolean(distributeChannel(rel)?.firebaseAppId || distributeChannel(rel)?.firebaseAppIdIos);
  // UCH-257: with a ref already saved, the picker stays unmounted (no /git/*
  // fetches) until the user asks to change it; once opened it stays open.
  const [editingRef, setEditingRef] = createSignal(false);
  // Description edit buffer; null = not editing. Editable in every status.
  const [editingDescription, setEditingDescription] = createSignal<string | null>(null);

  const promote = usePromoteRelease(props.releaseId);

  const can = (perm: string) => session()?.permissions.has(perm) ?? false;
  // UCH-419: release action gates honor scoped grants for this release's
  // product+environment (global keys still mean "all").
  const canCap = (cap: 'view' | 'create' | 'confirm' | 'publish') => {
    const rel = release.data?.data;
    if (!rel) return false;
    return canReleaseScope(can, cap, rel.productSlug, rel.environment);
  };

  // UCH-436: one-button promote — the next env in the pipeline, only when the
  // source worked and the caller may create drafts there (global or scoped).
  const promoteTarget = (rel: {
    status: string;
    environment: ReleaseEnvironment;
    productSlug: string;
  }): ReleaseEnvironment | null => {
    if (rel.status !== 'succeeded' && rel.status !== 'published') return null;
    const next = RELEASE_ENVIRONMENTS[RELEASE_ENVIRONMENTS.indexOf(rel.environment) + 1];
    return next && canReleaseScope(can, 'create', rel.productSlug, next) ? next : null;
  };

  const onPromote = async (targetEnv: ReleaseEnvironment) => {
    // UCH-515: promote only after an explicit confirm — it creates a real draft.
    const rel = release.data?.data;
    const ok = await confirmDialog({
      title: `Promote to ${targetEnv.toUpperCase()}?`,
      body: `Creates a new ${targetEnv.toUpperCase()} draft of ${rel?.version ?? 'this version'} pinned to the same commit with a new build number. Nothing builds until that draft is confirmed.`,
      confirmLabel: 'Promote',
    });
    if (!ok) return;
    try {
      const res = await promote.mutateAsync();
      toast.success(`Draft created in ${targetEnv.toUpperCase()}`);
      navigate(`/release/versions/${res.data.id}`);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  // UCH-515: Publish is irreversible (raises the version floor) — danger item
  // behind a danger confirm. A channel with a deploy target deploys first and
  // is marked published only once the deployment is healthy.
  const onPublish = async (rel: {
    productId: string;
    version: string;
    buildNumber: number;
    environment: string;
  }) => {
    const deploys = Boolean(distributeChannel(rel)?.deployConfig);
    const ok = await confirmDialog({
      title: 'Publish release?',
      body: `${deploys ? `Deploys build #${rel.buildNumber} to ${rel.environment.toUpperCase()}; once the deployment is healthy it is marked` : `Publishing marks build #${rel.buildNumber} as`} the final ${rel.version} in ${rel.environment.toUpperCase()} and permanently raises the version floor — equal or lower versions can never be confirmed again.`,
      confirmLabel: 'Publish',
      danger: true,
    });
    if (!ok) return;
    try {
      const { status, deployment } = (await publish.mutateAsync()).data;
      // Dispatch can fail on the spot: the release comes back succeeded with the error.
      if (status === 'succeeded' && deployment?.error)
        toast.error(`Deployment failed: ${deployment.error}`);
      else if (status === 'publishing') toast.success('Deployment started');
      else {
        // celebrate only here, so a failed publish stays quiet
        celebrate();
        toast.success('Release published');
      }
    } catch (err) {
      toast.error(errorMessage(err));
      release.refetch();
    }
  };

  // UCH-440: Copy Summary / Publish to testers / Promote collapse into one
  // Actions dropdown; each item keeps its original visibility condition.
  // UCH-515 adds Publish as a danger item.
  const actionItems = (rel: {
    status: string;
    environment: ReleaseEnvironment;
    productId: string;
    productSlug: string;
    selectedWorkflows: string[];
    version: string;
    buildNumber: number;
    readyToPublish: boolean;
  }): MenuItem[] => {
    const items: MenuItem[] = [];
    if (rel.status !== 'draft')
      items.push({ label: 'Copy Summary', icon: 'copy', onSelect: () => setCopyOpen(true) });
    if (canCap('publish') && canDistribute(rel))
      items.push({
        label: 'Publish to testers',
        icon: 'publish',
        onSelect: () => setDistributeOpen(true),
      });
    const target = promoteTarget(rel);
    if (target)
      items.push({
        label: `Promote to ${target.toUpperCase()}`,
        icon: 'link',
        onSelect: () => onPromote(target),
      });
    if (rel.status === 'succeeded' && canCap('publish'))
      items.push({
        label: 'Publish',
        icon: 'release',
        danger: true,
        disabled: !rel.readyToPublish,
        title: rel.readyToPublish ? undefined : 'Required artifacts are incomplete',
        onSelect: () => onPublish(rel),
      });
    if (rel.status === 'publishing' && canCap('publish'))
      items.push({
        label: 'Publishing…',
        icon: 'release',
        disabled: true,
        title: 'Deployment in progress',
        onSelect: () => {},
      });
    return items;
  };

  const act = async (fn: () => Promise<unknown>, success: string) => {
    try {
      await fn();
      toast.success(success);
    } catch (err) {
      toast.error(errorMessage(err));
      // UCH-504: a failed action often means the release changed under us
      // (e.g. confirmed elsewhere) — resync so stale editors collapse.
      release.refetch();
    }
  };

  const onDelete = async () => {
    const r = release.data?.data;
    if (!r) return;
    const ok = await confirmDialog({
      title: 'Delete draft?',
      body: `Permanently deletes draft ${r.version} #${r.buildNumber}.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await act(() => remove.mutateAsync(), 'Draft deleted');
    navigate('/release/versions');
  };

  return (
    <section>
      <Show
        when={!release.isPending}
        fallback={
          <div class="mx-auto max-w-6xl p-6">
            <Skeleton class="h-64" />
          </div>
        }
      >
        <Show
          when={release.data?.data}
          fallback={
            <div class="mx-auto max-w-6xl p-6">
              <EmptyState variant="error" title="Failed to load release" />
            </div>
          }
        >
          {(r) => (
            <>
              <PageHeader
                title={
                  <>
                    {r().productName} {r().version}{' '}
                    <span class="text-neutral-400">build #{r().buildNumber}</span>
                  </>
                }
                sub={
                  <div class="flex items-center gap-2">
                    <ReleaseStatusBadge status={r().status} stale={r().isStale} />
                    <EnvBadge env={r().environment} />
                  </div>
                }
                actions={
                  <>
                    <Show when={r().status === 'running' || r().status === 'publishing'}>
                      <LiveIndicator />
                    </Show>
                    <Show when={r().status !== 'draft'}>
                      <Button
                        variant="secondary"
                        title={
                          session()?.slackUserId
                            ? undefined
                            : 'Link Slack in your Personal settings to get DMs for this release'
                        }
                        onClick={() =>
                          act(
                            () => watch.mutateAsync(!r().watching),
                            r().watching ? 'Stopped watching' : 'Watching — you will get Slack DMs',
                          )
                        }
                        loading={watch.isPending}
                      >
                        {r().watching ? 'Unwatch' : 'Watch'}
                      </Button>
                    </Show>
                    <Show when={r().status === 'running' && canCap('confirm')}>
                      <Button
                        variant="secondary"
                        onClick={() => act(() => reconcile.mutateAsync(), 'Run states re-synced')}
                        loading={reconcile.isPending}
                      >
                        Reconcile
                      </Button>
                    </Show>
                    <Show when={actionItems(r()).length > 0}>
                      <Menu
                        items={actionItems(r())}
                        triggerClass={buttonClass('primary')}
                        trigger={
                          <>
                            Actions
                            <Icon name="angle-small-down" />
                          </>
                        }
                      />
                    </Show>
                    <Show when={r().status === 'draft' && canCap('create')}>
                      <Button variant="danger" onClick={onDelete} loading={remove.isPending}>
                        Delete draft
                      </Button>
                    </Show>
                  </>
                }
              />

              <Show when={distributeMounted()}>
                <DistributeModal
                  open={distributeOpen()}
                  onClose={() => setDistributeOpen(false)}
                  release={r()}
                  defaultGroups={distributeChannel(r())?.fadTesterGroups ?? []}
                />
              </Show>

              <Show when={r().status !== 'draft'}>
                <CopySummary release={r()} open={copyOpen()} onClose={() => setCopyOpen(false)} />
              </Show>

              <div class="mx-auto max-w-6xl p-6">
                <Show when={r().isStale}>
                  <p class="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                    This draft can no longer be released: version {r().version} is not newer than
                    the already-published version {r().versionFloor}. It can only be deleted.
                  </p>
                </Show>

                <dl class="grid grid-cols-2 gap-4 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark sm:grid-cols-3">
                  <Field label="Status">{r().status}</Field>
                  <Field label="Created by">
                    <UserInfo user={r().createdBy} />
                  </Field>
                  <Field label="Created">
                    <When date={r().createdAt} class="" />
                  </Field>
                  <Field label="Confirmed">
                    {r().confirmedAt ? <When date={r().confirmedAt} class="" /> : '—'}
                  </Field>
                  <Field label="Published">
                    {r().publishedAt ? <When date={r().publishedAt} class="" /> : '—'}
                  </Field>
                  <Field label="Commit SHA">
                    <Show
                      when={commitHref(r())}
                      fallback={<code class="text-xs">{r().commitSha || '—'}</code>}
                    >
                      {(href) => (
                        <a
                          href={href()}
                          target="_blank"
                          rel="noreferrer"
                          class="text-brand-600 hover:underline dark:text-brand-400"
                        >
                          <code class="text-xs">{r().commitSha}</code>
                        </a>
                      )}
                    </Show>
                  </Field>
                  <Show when={r().failureReason}>
                    <Field label="Failure">{r().failureReason}</Field>
                  </Show>
                  <Show when={r().autoDistributeGroups.length > 0}>
                    <Field label="Auto publish to testers">
                      {r().autoDistributeGroups.join(', ')}
                    </Field>
                  </Show>
                  <Field label="Description" class="col-span-full">
                    <Show
                      when={editingDescription() !== null}
                      fallback={
                        <>
                          <Show
                            when={canCap('create')}
                            fallback={
                              <span
                                class={r().description ? 'whitespace-pre-wrap' : 'text-neutral-400'}
                              >
                                {r().description || '—'}
                              </span>
                            }
                          >
                            <button
                              type="button"
                              title="Click to edit"
                              aria-label="Edit description"
                              class="group inline-flex max-w-full items-start gap-1.5 text-left"
                              onClick={() => setEditingDescription(r().description)}
                            >
                              <span
                                class={r().description ? 'whitespace-pre-wrap' : 'text-neutral-400'}
                              >
                                {r().description || '—'}
                              </span>
                              <Icon
                                name="pencil"
                                class="mt-1 h-3 w-3 text-neutral-400 transition-colors group-hover:text-brand-600 motion-reduce:transition-none dark:group-hover:text-brand-400"
                              />
                            </button>
                          </Show>
                          <Show when={r().descriptionHistory[0]}>
                            {(latest) => (
                              <p class="mt-0.5 text-xs text-neutral-400">
                                by {latest().by?.displayName ?? 'unknown'} ·{' '}
                                <When date={latest().at} class="" />
                              </p>
                            )}
                          </Show>
                          <Show when={r().descriptionHistory.length > 1}>
                            <details class="mt-1 text-xs text-neutral-500">
                              <summary class="cursor-pointer select-none">
                                History ({r().descriptionHistory.length})
                              </summary>
                              <ul class="mt-1 space-y-1 border-l border-neutral-200 pl-3 dark:border-neutral-800">
                                <For each={r().descriptionHistory}>
                                  {(h) => (
                                    <li>
                                      <span class="whitespace-pre-wrap">
                                        {h.description || '—'}
                                      </span>{' '}
                                      <span class="text-neutral-400">
                                        — {h.by?.displayName ?? 'unknown'} ·{' '}
                                        <When date={h.at} class="" />
                                      </span>
                                    </li>
                                  )}
                                </For>
                              </ul>
                            </details>
                          </Show>
                        </>
                      }
                    >
                      <div class="flex flex-col gap-2">
                        <Textarea
                          aria-label="Description"
                          rows={2}
                          maxLength={255}
                          hint={`${(editingDescription() ?? '').length}/255`}
                          value={editingDescription() ?? ''}
                          onInput={(e) => setEditingDescription(e.currentTarget.value)}
                        />
                        <div class="flex gap-2">
                          <Button
                            size="sm"
                            loading={update.isPending}
                            onClick={() =>
                              act(async () => {
                                await update.mutateAsync({
                                  description: (editingDescription() ?? '').trim(),
                                });
                                setEditingDescription(null);
                              }, 'Description saved')
                            }
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingDescription(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </Show>
                  </Field>
                </dl>

                {/* UCH-436: promotion chain — hidden when the release has no promote links. */}
                <PromotionTrailCard trail={r().promotionTrail} currentId={r().id} />

                {/* Requirements card hidden for now (UCH-435); component + API kept. */}

                <Show
                  when={r().status === 'draft' && !r().isStale && canCap('create')}
                  fallback={
                    <>
                      <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                        <h2 class="mb-2 font-medium">Git ref</h2>
                        <Show
                          when={r().commitSha}
                          fallback={<p class="text-sm text-neutral-600">{r().ref || '—'}</p>}
                        >
                          <div class={selectedTileClass}>
                            <span class="truncate font-medium">
                              {r().ref || r().commitSha.slice(0, 7)}
                            </span>
                            <div class="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
                              <Show
                                when={r().commitUrl}
                                fallback={<code>{r().commitSha.slice(0, 7)}</code>}
                              >
                                <a
                                  href={r().commitUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  class="text-brand-600 hover:underline dark:text-brand-400"
                                >
                                  <code>{r().commitSha.slice(0, 7)}</code>
                                </a>
                              </Show>
                              <span class="truncate">{r().commitSubject}</span>
                            </div>
                          </div>
                        </Show>
                      </div>
                      <Show when={r().selectedWorkflows.length > 0}>
                        <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                          <h2 class="mb-2 font-medium">Workflows</h2>
                          <ul class="flex flex-col gap-2">
                            <For each={r().selectedWorkflows}>
                              {(file) => (
                                <li class={selectedTileClass}>
                                  <span class="truncate font-medium">
                                    {workflowTitle(r().productId, file) ?? file}
                                  </span>
                                  {/* Filename line only when the title isn't already the filename. */}
                                  <Show when={workflowTitle(r().productId, file)}>
                                    <div class="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
                                      <code>{file}</code>
                                    </div>
                                  </Show>
                                  <Show
                                    when={
                                      Object.keys(r().workflowInputOverrides[file] ?? {}).length > 0
                                    }
                                  >
                                    <div class="mt-1 flex flex-wrap gap-1.5">
                                      <For
                                        each={Object.entries(
                                          r().workflowInputOverrides[file] ?? {},
                                        )}
                                      >
                                        {([k, v]) => (
                                          <code class="rounded border border-neutral-200 bg-neutral-50 px-1.5 py-0.5 text-xs dark:border-neutral-800 dark:bg-neutral-900/40">
                                            {k}={v}
                                          </code>
                                        )}
                                      </For>
                                    </div>
                                  </Show>
                                </li>
                              )}
                            </For>
                          </ul>
                          <InjectedVars
                            version={r().version}
                            buildNumber={r().buildNumber}
                            environment={r().environment}
                            commitSha={r().commitSha}
                          />
                        </div>
                      </Show>
                    </>
                  }
                >
                  <StepSection n={1} title="Git ref">
                    {/* UCH-436: a promoted draft is pinned to its source ref+commit — read-only.
                    Pinless sources (legacy ref='') leave the draft editable. */}
                    <Show
                      when={!r().refLocked}
                      fallback={
                        <>
                          <Show when={savedRefSelection(r())}>
                            {(sel) => (
                              <SelectedRefCard productId={r().productId} selection={sel()} />
                            )}
                          </Show>
                          <p class="mt-3 flex items-center gap-1.5 text-xs text-neutral-500">
                            <Icon name="lock" class="h-3.5 w-3.5" />
                            Locked — a promoted draft builds the same commit as its source release.
                          </p>
                        </>
                      }
                    >
                      <Show when={editingRef() || !savedRefSelection(r())}>
                        {/* Mounts only when open so the tab queries don't fire for an
                        already-pinned draft; @starting-style animates the reveal. */}
                        <div class="grid grid-rows-[1fr] opacity-100 transition-[grid-template-rows,opacity] duration-200 ease-out starting:grid-rows-[0fr] starting:opacity-0 motion-reduce:transition-none">
                          <div class="min-h-0 overflow-hidden">
                            <RefPicker
                              productId={r().productId}
                              value={pendingRef() ?? savedRefSelection(r())}
                              onChange={setPendingRef}
                            />
                          </div>
                        </div>
                      </Show>
                      <Show when={pendingRef() ?? savedRefSelection(r())}>
                        {(sel) => (
                          <div class="mt-3">
                            <SelectedRefCard productId={r().productId} selection={sel()} />
                          </div>
                        )}
                      </Show>
                      <div class="mt-3 flex justify-end gap-2">
                        <Show when={r().ref && r().commitSha}>
                          <Button
                            variant="secondary"
                            loading={refreshRef.isPending}
                            onClick={() =>
                              act(() => refreshRef.mutateAsync(), 'Commit SHA re-pinned from ref')
                            }
                          >
                            Refresh SHA
                          </Button>
                        </Show>
                        <Show
                          when={editingRef() || !savedRefSelection(r())}
                          fallback={
                            <Button variant="secondary" onClick={() => setEditingRef(true)}>
                              Change ref
                            </Button>
                          }
                        >
                          <Button
                            loading={update.isPending}
                            disabled={
                              !pendingRef() ||
                              pendingRef()?.commitSha === r().commitSha ||
                              confirm.isPending
                            }
                            onClick={async () => {
                              const sel = pendingRef();
                              if (!sel) return;
                              // Changing the ref invalidates the workflow selection — reset atomically.
                              await act(
                                () =>
                                  update.mutateAsync({
                                    ref: sel.ref,
                                    commitSha: sel.commitSha,
                                    selectedWorkflows: [],
                                    workflowInputOverrides: {},
                                  }),
                                'Ref pinned — workflow selection reset',
                              );
                              setPendingRef(null);
                            }}
                          >
                            Save ref
                          </Button>
                        </Show>
                      </div>
                    </Show>
                  </StepSection>

                  <StepSection
                    n={2}
                    title="Workflows to run"
                    disabled={!r().commitSha}
                    hint="pin a ref first"
                  >
                    <WorkflowsSection
                      productId={r().productId}
                      environment={r().environment}
                      version={r().version}
                      buildNumber={r().buildNumber}
                      commitSha={r().commitSha}
                      selected={r().selectedWorkflows}
                      overrides={r().workflowInputOverrides}
                      autoDistributeGroups={r().autoDistributeGroups}
                      canEdit={true}
                      saving={update.isPending || confirm.isPending}
                      onSave={(selectedWorkflows, workflowInputOverrides, autoDistributeGroups) =>
                        act(
                          () =>
                            update.mutateAsync({
                              selectedWorkflows,
                              workflowInputOverrides,
                              autoDistributeGroups,
                            }),
                          'Workflows saved',
                        )
                      }
                    />
                  </StepSection>
                </Show>

                <Show
                  when={r().status === 'draft' && !r().isStale && canCap('create')}
                  fallback={<ChangeLogTabs releaseId={props.releaseId} status={r().status} />}
                >
                  <StepSection
                    n={3}
                    title="Compare changes"
                    disabled={r().selectedWorkflows.length === 0}
                    hint="select workflows first"
                  >
                    {/* Show, not just disabled: unmounting ChangeLogTabs keeps the
                      changelog query from firing before workflows are chosen. */}
                    <Show
                      when={r().selectedWorkflows.length > 0}
                      fallback={
                        <p class="text-sm text-neutral-500">
                          Select workflows to run first (step 2).
                        </p>
                      }
                    >
                      <BaselineSection
                        releaseId={props.releaseId}
                        baseReleaseId={r().baseReleaseId}
                        canEdit={true}
                        saving={update.isPending || confirm.isPending}
                        onSave={(baseReleaseId) =>
                          act(() => update.mutateAsync({ baseReleaseId }), 'Baseline updated')
                        }
                      />
                      <ChangeLogTabs releaseId={props.releaseId} status={r().status} />
                    </Show>
                  </StepSection>

                  <Show when={canCap('confirm')}>
                    <StepSection
                      n={4}
                      title="Confirm"
                      disabled={!r().commitSha || r().selectedWorkflows.length === 0}
                      hint={!r().commitSha ? 'pin a ref first' : 'select workflows first'}
                    >
                      <p class="mb-3 text-sm text-neutral-600 dark:text-neutral-300">
                        Confirm triggers every selected workflow at the pinned commit. After Confirm
                        the release is immutable.
                      </p>
                      <Button
                        onClick={() =>
                          act(
                            () => confirm.mutateAsync(),
                            'Release confirmed — workflows triggered',
                          )
                        }
                        loading={confirm.isPending}
                        disabled={!r().commitSha || r().selectedWorkflows.length === 0}
                      >
                        Confirm
                      </Button>
                    </StepSection>
                  </Show>
                </Show>

                {/* UCH-423: Publish-to-testers history, newest first. */}
                <Show when={r().distributions.length > 0}>
                  <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                    <h2 class="mb-2 font-medium">Publish to testers history</h2>
                    <ul class="flex flex-col gap-2">
                      <For each={r().distributions}>
                        {(d) => (
                          <li class={selectedTileClass}>
                            <div class="flex items-center justify-between gap-2">
                              <span class="flex min-w-0 items-center gap-1.5 truncate font-medium">
                                by{' '}
                                <Show when={d.by} fallback="unknown">
                                  {(by) => <UserInfo user={by()} />}
                                </Show>
                              </span>
                              <When date={d.at} class="shrink-0 text-xs text-neutral-500" />
                            </div>
                            <div class="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                              <span>→ {d.groupAliases.join(', ')}</span>
                              <For each={d.platforms}>
                                {(platform) => <Badge tone="neutral">{platform}</Badge>}
                              </For>
                            </div>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>

                {/* No artifacts → no card (UCH-435); the missing-required banner
                  only exists while at least one artifact is shown. */}
                <Show when={r().artifacts.length > 0}>
                  <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                    <h2 class="mb-2 font-medium">Artifacts</h2>
                    <Show when={r().missingArtifacts.length > 0}>
                      <p class="mb-2 text-sm text-amber-700 dark:text-amber-300">
                        Missing required artifacts:{' '}
                        {r()
                          .missingArtifacts.map((m) => `${m.platform}/${m.arch}`)
                          .join(', ')}
                      </p>
                    </Show>
                    <ul class="flex flex-col gap-2">
                      <For each={r().artifacts}>
                        {(a) => (
                          <li class={selectedTileClass}>
                            <div class="flex items-center justify-between gap-2">
                              <code class="truncate font-medium">{a.filename}</code>
                              <Show when={a.url}>
                                <a
                                  href={a.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  class="shrink-0 text-brand-600 hover:underline dark:text-brand-400"
                                >
                                  Download
                                </a>
                              </Show>
                            </div>
                            <div class="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-neutral-500">
                              <Badge tone="neutral">
                                {a.platform}/{a.arch}
                              </Badge>
                              <span>{(a.sizeBytes / 1024 / 1024).toFixed(1)} MB</span>
                              <Show when={a.packageId}>
                                <span>{a.packageId}</span>
                              </Show>
                            </div>
                          </li>
                        )}
                      </For>
                    </ul>
                  </div>
                </Show>

                <Show when={r().runs.length > 0}>
                  <div class="mt-6 rounded-xl border border-neutral-200 bg-surface p-4 dark:border-neutral-800 dark:bg-surface-dark">
                    <h2 class="mb-2 font-medium">Workflow runs</h2>
                    <ul class="flex flex-col gap-2">
                      <For each={r().runs}>
                        {(run) => (
                          <li class={selectedTileClass}>
                            <div class="flex items-center justify-between gap-2">
                              <span class="truncate font-medium">
                                {workflowTitle(r().productId, run.workflowFile) ?? run.workflowFile}
                              </span>
                              <span class="flex shrink-0 items-center gap-2">
                                <When date={run.updatedAt} />
                                <Badge
                                  tone={
                                    run.status === 'succeeded'
                                      ? 'success'
                                      : run.status === 'failed'
                                        ? 'danger'
                                        : 'info'
                                  }
                                >
                                  {run.status}
                                </Badge>
                                <Show
                                  when={
                                    run.status === 'failed' &&
                                    run.githubRunId != null &&
                                    canCap('confirm')
                                  }
                                >
                                  <Button
                                    size="sm"
                                    variant="secondary"
                                    loading={retryRun.isPending}
                                    onClick={async () => {
                                      const ok = await confirmDialog({
                                        title: 'Retry failed jobs?',
                                        body: `Re-runs only the failed jobs of ${run.workflowFile} on GitHub (same build, attempt #${run.githubRunAttempt + 1}); the release returns to Running until every workflow finishes.`,
                                        confirmLabel: 'Retry',
                                      });
                                      if (ok)
                                        await act(
                                          () => retryRun.mutateAsync(run.id),
                                          'Retry dispatched',
                                        );
                                    }}
                                  >
                                    Retry
                                  </Button>
                                </Show>
                                <Show when={previewUrl(r(), run)}>
                                  {(href) => (
                                    <a
                                      href={href()}
                                      target="_blank"
                                      rel="noreferrer"
                                      class="text-brand-600 hover:underline dark:text-brand-400"
                                    >
                                      Open preview
                                    </a>
                                  )}
                                </Show>
                                {/* Exact run URL once webhook/reconcile claims it; the
                                  workflow's Actions page until then. */}
                                <Show
                                  when={
                                    run.htmlUrl ||
                                    workflowFallbackUrl(r().productId, run.workflowFile)
                                  }
                                >
                                  {(href) => (
                                    <a
                                      href={href()}
                                      target="_blank"
                                      rel="noreferrer"
                                      class="text-brand-600 hover:underline dark:text-brand-400"
                                    >
                                      View on GitHub
                                    </a>
                                  )}
                                </Show>
                              </span>
                            </div>
                            <Show when={workflowTitle(r().productId, run.workflowFile)}>
                              <div class="mt-0.5 flex items-center gap-2 text-xs text-neutral-500">
                                <code>{run.workflowFile}</code>
                              </div>
                            </Show>
                            <Show when={run.failureDetail}>
                              {(detail) => <RunFailurePanel detail={detail()} />}
                            </Show>
                            <Show when={run.attempts.length > 0}>
                              <RunAttemptsPanel
                                releaseId={r().id}
                                runId={run.id}
                                attempts={run.attempts}
                              />
                            </Show>
                          </li>
                        )}
                      </For>
                      {/* Publish = deploy: the latest deployment, shown as the last run. */}
                      <Show when={r().deployment}>
                        {(d) => (
                          <li class={selectedTileClass}>
                            <div class="flex items-center justify-between gap-2">
                              <span class="truncate font-medium">Deployment</span>
                              <span class="flex shrink-0 items-center gap-2">
                                <When date={d().finishedAt ?? d().startedAt} />
                                <Show when={d().argocdPhase}>
                                  {(phase) => (
                                    <Badge
                                      tone={
                                        phase() === 'healthy'
                                          ? 'success'
                                          : phase() === 'failed'
                                            ? 'danger'
                                            : 'neutral'
                                      }
                                    >
                                      ArgoCD {phase()}
                                    </Badge>
                                  )}
                                </Show>
                                <Badge
                                  tone={
                                    d().status === 'succeeded'
                                      ? 'success'
                                      : d().status === 'failed'
                                        ? 'danger'
                                        : 'info'
                                  }
                                >
                                  {d().status}
                                </Badge>
                                <a
                                  href={d().runUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  class="text-brand-600 hover:underline dark:text-brand-400"
                                >
                                  View on GitHub
                                </a>
                              </span>
                            </div>
                            <Show when={d().expectedRevision}>
                              {(sha) => (
                                <div class="mt-0.5 text-xs text-neutral-500">
                                  revision <code>{sha().slice(0, 7)}</code>
                                </div>
                              )}
                            </Show>
                            <Show when={d().error}>
                              <p class="mt-1 text-sm text-red-600 dark:text-red-400">{d().error}</p>
                            </Show>
                          </li>
                        )}
                      </Show>
                    </ul>
                  </div>
                </Show>
              </div>
            </>
          )}
        </Show>
      </Show>
    </section>
  );
}
